import React, { useState, useEffect, useRef } from "react";
import html2pdf from "html2pdf.js";
import Swal from "sweetalert2";
import axios from "axios";

const daysOfWeek = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const roles = [
  "Assistant Professor",
  "Senior Registrar (ECHO OPD)",
  "Ward Echo",
  "Senior Registrar (Surgical On-call Echo)"
];

const generatePDFBlob = async ({ language, month, days }) => {
  const isUrdu = language === "ur";

  const urduDays = {
    Monday: "پیر",
    Tuesday: "منگل",
    Wednesday: "بدھ",
    Thursday: "جمعرات",
    Friday: "جمعہ",
    Saturday: "ہفتہ"
  };

  const urduRoles = {
    "Assistant Professor": "اسسٹنٹ پروفیسر",
    "Senior Registrar (ECHO OPD)": "سینئر رجسٹرار (ایکو او پی ڈی)",
    "Ward Echo": "وارڈ ایکو",
    "Senior Registrar (Surgical On-call Echo)": "سینئر رجسٹرار (سرجیکل آن کال ایکو)"
  };

  const hospitalName = isUrdu ? "راولپنڈی انسٹیٹیوٹ آف کارڈیالوجی" : "Rawalpindi Institute of Cardiology";
  const subAddress = isUrdu ? "راول روڈ، راولپنڈی" : "Rawal Road, Rawalpindi";
  const title = isUrdu ? "کنسلٹنٹ ایکو روسٹر" : "Consultant Echo Roster";

  const leftLogoUrl = "../../assets/img/logo/new-logo.png";
  const rightLogoUrl = "../../assets/img/logo/punjab-logo.png";

  const rolesHeader = roles
    .map(role => `<th>${isUrdu ? (urduRoles[role] || role) : role}</th>`)
    .join("");

 const autoTranslateToUrdu = async (text) => {
  if (!isUrdu || !text?.trim()) return text;

  try {
    const res = await fetch(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=ur&dt=t&q=${encodeURIComponent(text)}`);
    const json = await res.json();
    return json[0]?.map(item => item[0]).join("") || text;
  } catch (err) {
    console.error("Translation error:", err);
    return text;
  }
};

  const rows = await Promise.all(
    daysOfWeek.map(async (day, index) => {
      const bg = index % 2 === 0 ? "#f4f6fb" : "#ffffff";
      const cells = await Promise.all(
        roles.map(async (role) => {
          const value = days?.[day]?.[role] || "";
          const translated = await autoTranslateToUrdu(value);
          return `<td>${translated}</td>`;
        })
      );

      const dayTranslated = isUrdu ? (urduDays[day] || day) : day;

      return `<tr style="background-color: ${bg};">
                <td>${dayTranslated}</td>${cells.join("")}
              </tr>`;
    })
  );

  const content = `
    <div style="font-family: ${isUrdu ? "'Noto Nastaliq Urdu'" : "'Segoe UI', sans-serif"};
                direction: ${isUrdu ? "rtl" : "ltr"}; padding: 20px; font-size: 13px; color: #2c3e50;">
      <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
        <div style="width: 20%;"><img src="${leftLogoUrl}" style="height: 60px;" /></div>
        <div style="text-align: center; width: 60%;">
          <h2 style="margin: 0; font-weight: 700;">${hospitalName}</h2>
          <p style="margin: 2px 0; font-size: 13px; color: #555;">${subAddress}</p>
          <h4 style="margin: 6px 0; color: #444;">${title} – ${month}</h4>
        </div>
        <div style="width: 20%; text-align: ${isUrdu ? "left" : "right"};">
          <img src="${rightLogoUrl}" style="height: 60px;" />
        </div>
      </div>

      <table style="width: 100%; border-collapse: collapse; font-size: 12px; text-align: center;">
        <thead>
          <tr style="background: linear-gradient(to right, #0052D4, #4364F7); color: #fff;">
            <th style="padding: 10px;">${isUrdu ? "دن" : "Day"}</th>
            ${rolesHeader}
          </tr>
        </thead>
        <tbody>
          ${rows.join("")}
        </tbody>
      </table>

      <div style="margin-top: 80px; display: flex; justify-content: center; gap: 80px; font-size: 13px;">
        <div style="text-align: center;">
          <div style="border-top: 1px solid #333; width: 220px; margin: auto; margin-bottom: 6px;"></div>
          <strong>Dr. Hamza Azhar Ghauri</strong><br />
          <span>${isUrdu ? "اسسٹنٹ پروفیسر / روسٹر انچارج" : "Assistant Professor / Roster Incharge"}</span><br />
          <span>${hospitalName}</span>
        </div>
        <div style="text-align: center;">
          <div style="border-top: 1px solid #333; width: 220px; margin: auto; margin-bottom: 6px;"></div>
          <strong>Dr. Asim Javed</strong><br />
          <span>${isUrdu ? "ایسوسی ایٹ پروفیسر / ایچ او ڈی کارڈیالوجی" : "Associate Professor / HOD Cardiology"}</span><br />
          <span>${hospitalName}</span>
        </div>
      </div>

      <p style="text-align:center; margin-top: 40px; font-size: 10px; color: #888;">
        © ${new Date().getFullYear()} ${hospitalName}. All rights reserved.
      </p>
    </div>
  `;

  return await html2pdf()
    .from(content)
    .set({
      margin: 0.5,
      filename: `Echo_Roster_${month}_${language.toUpperCase()}.pdf`,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "a4", orientation: "landscape" }
    })
    .output("blob");
};


const ConsultantEchoRoster = () => {
  const [days, setDays] = useState({});
  const [month, setMonth] = useState(new Date().toLocaleString("default", { month: "long", year: "numeric" }));
  const [selectedMonth, setSelectedMonth] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [activeCell, setActiveCell] = useState(null);
  const inputRefs = useRef({});

  useEffect(() => {
    if (selectedMonth) loadRoster(selectedMonth);
  }, [selectedMonth]);

const updateValue = (day, role, rawValue) => {
  // Auto-add a space after every comma, remove extra spaces
 const value = rawValue
  .replace(/,\s*/g, ', ')  // Ensure one space after commas
  .replace(/\s{2,}/g, ' ') // Remove extra spaces
  .trim();

  setDays(prev => ({ ...prev, [day]: { ...prev[day], [role]: value } }));

  const lastWord = value.split(/[\s,]+/).pop().trim();
  if (lastWord.length >= 1) {
    axios.get(`https://ricgop.online/backend/getCerNames.php?name=${encodeURIComponent(lastWord)}`)
      .then((res) => setSuggestions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setSuggestions([]));
  } else {
    setSuggestions([]);
  }

  setHighlightedIndex(-1);
  setActiveCell({ day, role });
};

  

  const saveRoster = async () => {
    try {
      const departments = {};
      roles.forEach(role => {
        departments[role] = {};
        daysOfWeek.forEach(day => {
          departments[role][day] = days[day]?.[role] || "";
        });
      });
      await axios.post("https://ricgop.online/backend/consultant_echo_roster.php", { month, departments });
      Swal.fire("✅ Saved", "Roster saved successfully.", "success");
    } catch {
      Swal.fire("❌ Error", "Failed to save roster.", "error");
    }
  };

  const loadRoster = async (m) => {
    try {
      const res = await axios.get("https://ricgop.online/backend/consultant_echo_roster.php", { params: { month: m } });
      if (res.data.success) {
        const departments = res.data.departments || {};
        const transformed = {};
        for (const role in departments) {
          for (const day in departments[role]) {
            if (!transformed[day]) transformed[day] = {};
            transformed[day][role] = departments[role][day];
          }
        }
        setDays(transformed);
        setMonth(m);
        Swal.fire("✅ Loaded", "Roster loaded.", "success");
      } else {
        Swal.fire("⚠️ Not Found", "No roster found for this month.", "warning");
        setDays({});
      }
    } catch {
      Swal.fire("❌ Error", "Failed to load roster.", "error");
    }
  };

  const downloadPDFs = async () => {
    Swal.fire({ title: 'Generating...', allowOutsideClick: false, didOpen: () => Swal.showLoading() });
    try {
      const [enBlob, urBlob] = await Promise.all([
        generatePDFBlob({ language: "en", month, days }),
        generatePDFBlob({ language: "ur", month, days }),
      ]);
      const link = document.createElement("a");
      link.href = URL.createObjectURL(enBlob);
      link.download = `Echo_Roster_${month}_EN.pdf`;
      link.click();

      const link2 = document.createElement("a");
      link2.href = URL.createObjectURL(urBlob);
      link2.download = `Echo_Roster_${month}_UR.pdf`;
      link2.click();

      Swal.fire("✅ Success", "PDFs downloaded.", "success");
    } catch {
      Swal.fire("❌ Error", "PDF generation failed.", "error");
    }
  };

  const emailRoster = async () => {
    try {
      Swal.fire({ title: "Preparing PDFs...", html: "Please wait <b id='progress'>0</b>%", showConfirmButton: false, allowOutsideClick: false });
      const enBlob = await generatePDFBlob({ language: "en", month, days });
      const urBlob = await generatePDFBlob({ language: "ur", month, days });
      const formData = new FormData();
      formData.append("englishPDF", new File([enBlob], `Echo_Roster_${month}_EN.pdf`, { type: "application/pdf" }));
      formData.append("urduPDF", new File([urBlob], `Echo_Roster_${month}_UR.pdf`, { type: "application/pdf" }));
      formData.append("month", month);
      await axios.post("https://ricgop.online/backend/email_consultant_echo_roster.php", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded * 100) / e.total);
          const el = document.getElementById("progress");
          if (el) el.textContent = percent.toString();
        },
      });
      Swal.fire("📧 Emailed", "Roster sent successfully!", "success");
    } catch {
      Swal.fire("❌ Error", "Failed to send email.", "error");
    }
  };

  const handleKeyDown = (e, day, role) => {
    if (suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
    } else if (e.key === "Enter" && highlightedIndex >= 0) {
      e.preventDefault();
      insertSuggestion(suggestions[highlightedIndex], day, role);
    } else if (e.key === "Escape") {
      setSuggestions([]);
      setHighlightedIndex(-1);
    }
  };

  const insertSuggestion = (name, day, role) => {
    const input = inputRefs.current[`${day}-${role}`];
    const currentText = days[day]?.[role] || "";
    const cursor = input?.selectionStart || 0;
    const match = currentText.slice(0, cursor).match(/[\w\s]*$/);
    const insertStart = match ? cursor - match[0].length : cursor;

    const textBefore = currentText.slice(0, insertStart);
    const textAfter = currentText.slice(cursor);

    const separator = textBefore.trim().endsWith(",") || textBefore.trim() === "" ? "" : ", ";
    const newText = textBefore + separator + name + textAfter;

    updateValue(day, role, newText.trimStart());
    setTimeout(() => input?.focus(), 0);
    setSuggestions([]);
    setHighlightedIndex(-1);
  };

  return (
    <div className="container bg-light p-4 rounded-4 shadow border">
      <h4 className="text-center mb-3">🩺 Consultant Echo Roster</h4>
      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="form-label fw-semibold">📅 Roster Month</label>
          <select className="form-select" value={month} onChange={(e) => setMonth(e.target.value)}>
            {Array.from({ length: 12 }, (_, i) => {
              const d = new Date(); d.setMonth(d.getMonth() + i);
              const label = d.toLocaleString("default", { month: "long", year: "numeric" });
              return <option key={i} value={label}>{label}</option>;
            })}
          </select>
        </div>
        <div className="col-md-6">
          <label className="form-label fw-semibold">🔁 Load Existing</label>
          <select className="form-select" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
            <option value="">-- Select Month --</option>
            {Array.from({ length: 19 }, (_, i) => {
              const d = new Date(); d.setMonth(d.getMonth() - 12 + i);
              const label = d.toLocaleString("default", { month: "long", year: "numeric" });
              return <option key={i} value={label}>{label}</option>;
            })}
          </select>
        </div>
      </div>

      <div className="table-responsive">
        <table className="table table-bordered">
          <thead className="table-light"><tr><th>Day</th>{roles.map(role => <th key={role}>{role}</th>)}</tr></thead>
          <tbody>
            {daysOfWeek.map(day => (
              <tr key={day}>
                <td><strong>{day}</strong></td>
                {roles.map(role => (
                  <td key={role} style={{ position: "relative" }}>
                    <input
                      className="form-control"
                      value={days[day]?.[role] || ""}
                      onChange={(e) => updateValue(day, role, e.target.value)}
                      onKeyDown={(e) => handleKeyDown(e, day, role)}
                      placeholder="Write here"
                      ref={(el) => inputRefs.current[`${day}-${role}`] = el}
                    />
                    {activeCell?.day === day && activeCell?.role === role && suggestions.length > 0 && (
                      <ul className="list-group position-absolute w-100 shadow-sm z-3" style={{ top: "100%", left: 0, maxHeight: 200, overflowY: "auto" }}>
                        {suggestions.map((s, idx) => (
                          <li key={idx}
                              className={`list-group-item list-group-item-action ${idx === highlightedIndex ? "active" : ""}`}
                              onMouseDown={() => insertSuggestion(s, day, role)}>
                            {s}
                          </li>
                        ))}
                      </ul>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="my-3">
        <button className="btn btn-success me-2" onClick={saveRoster}>💾 Save</button>
        <button className="btn btn-primary me-2" onClick={downloadPDFs}>📥 Download</button>
        <button className="btn btn-outline-success" onClick={emailRoster}>📧 Email</button>
      </div>
    </div>
  );
};

export default ConsultantEchoRoster;
